import React, { useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { ChevronRight } from '@mui/icons-material';

import { formatWorkHoursRange, inferEndDayOffset } from '../../../../data-access';
import { useGeneralSettings } from '../../../../hooks';
import './SchedulerWorkHourPref.scss';
import { WorkHourPrefItemState } from '../reducer';
import { StartEndTimePickerModal } from '../../common';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface SchedulerWorkHourPrefProps {
  workHours: WorkHourPrefItemState;
  onWorkHourChange: (pref: WorkHourPrefItemState) => void;
  isLoading?: boolean;
  className?: string;
  fontSize?: 'small' | 'medium' | 'large';
  hasConfigured?: boolean;
  isInSettingsModal?: boolean;
}

export const SchedulerWorkHourPref: React.FC<SchedulerWorkHourPrefProps> = ({
  workHours,
  onWorkHourChange,
  isLoading,
  className,
  fontSize,
  hasConfigured,
  isInSettingsModal = false,
}) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);

  const handleOpenModal = () => {
    setStartTime(dayjs(workHours.start.time, 'HH:mm'));
    setEndTime(dayjs(workHours.end.time, 'HH:mm'));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSave = (startTime: Dayjs | null, endTime: Dayjs | null) => {
    if (!startTime || !endTime) {
      return;
    }

    const startHHmm = startTime.format('HH:mm');
    const endHHmm = endTime.format('HH:mm');

    onWorkHourChange({
      start: {
        ...workHours.start,
        time: startHHmm,
      },
      end: {
        ...workHours.end,
        time: endHHmm,
      },
      endDayOffset: inferEndDayOffset(startHHmm, endHHmm, workHours.endDayOffset),
    });

    handleCloseModal();
  };

  return (
    <div
      className={clsx('scheduler-work-hour-pref', className, {
        'scheduler-work-hour-pref--has-configured': hasConfigured,
        'scheduler-work-hour-pref--in-settings-modal': isInSettingsModal,
      })}
    >
      <div className="scheduler-work-hour-pref__list">
        {isLoading ? (
          <div className="scheduler-work-hour-pref__loading">
            <CircularProgress size={16} />
          </div>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="scheduler-work-hour-pref__list-item-ff"
            borderRadius={4}
            p={2}
            onClick={handleOpenModal}
          >
            <Typography variant="body2" className="scheduler-work-hour-pref__list-item-ff__title">
              ⏰ {t('settings.aiAssistant.workHours')}
            </Typography>
            <Stack direction="row" alignItems="center">
              <Typography
                variant={isInSettingsModal ? 'subtitle1' : 'caption'}
                className="scheduler-work-hour-pref__list-item-ff__duration"
              >
                {formatWorkHoursRange(
                  workHours.start.time,
                  workHours.end.time,
                  timeFormat,
                  workHours.endDayOffset
                )}
              </Typography>
              <ChevronRight className="scheduler-work-hour-pref__list-item-ff__icon" />
            </Stack>
          </Stack>
        )}
      </div>
      <StartEndTimePickerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        initialStartTime={startTime}
        initialEndTime={endTime}
        timeFormat={timeFormat}
        title={`🕒 ${t('settings.aiAssistant.setWorkHours')}`}
      />
    </div>
  );
};

export default SchedulerWorkHourPref;
